import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Badge,
  Button,
  FormControl,
  InputAdornment,
  MenuItem,
  Select,
  Slider,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@material-ui/core"
import { ExpandMore, Search } from "@material-ui/icons"
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown"
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp"
import clsx from "clsx"
import PropTypes from "prop-types"
import React, { useContext, useEffect, useMemo, useState } from "react"
import { useQuery } from "react-query"
import { useDebounce } from "use-lodash-debounce"
import { listUserClients } from "~/actions/clients"
import PermissionsWrapper from "~/components/Common/PermissionsWrapper"
import { ACCOUNT_CREATION_OPTIONS, SORT_OPTIONS } from "~/constants/appUsers"
import AppContext from "~/contexts/App"
import UsersContext from "~/contexts/Users"

import Box from "~/components/Common/Box"
import useStyles from "./styles"

const UsersFilter = ({
  totalNumber,
  totalShowing,
  setAccordionExpanded,
  accordionExpanded,
  currentPage,
  setCurrentPage,
}) => {
  const classes = useStyles()
  const theme = useTheme()

  const smDown = useMediaQuery(theme.breakpoints.down("sm"))

  const {
    searchValue,
    setSearchValue,
    activeValue,
    setActiveValue,
    clientValue,
    setClientValue,
    sliderValue,
    setSliderValue,
    coursesCount,
    debouncedSliderValue,
    setDebouncedSliderValue,
    userTypeValue,
    setUserTypeValue,
    sort,
    setSort,
    publishedCoursesCountMutation,
    accountCreationValue,
    setAccountCreationValue,
  } = useContext(UsersContext)

  const { openDialog } = useContext(AppContext)

  //Clients for filter clients
  const listClientsQuery = useQuery(["userClients"], () => listUserClients())

  // const maxPublishedCoursesCount = useQuery(["maxPublishedCourses"], () =>
  //   publishedCoursesCount()
  // )
  // useEffect(() => {
  //   setSliderValue([0, maxPublishedCoursesCount.data])
  // }, [maxPublishedCoursesCount])

  const clients = useMemo(() => {
    const clientsList = listClientsQuery.data
    clientsList?.sort((a, b) => a.name.localeCompare(b.name))
    return clientsList || []
  }, [listClientsQuery])

  //search state in this component
  const [searchComponentValue, setSearchComponentValue] = useState("")
  //badge value
  const [badgeState, setBadgeState] = useState(0)
  //active filters array
  const [activeFilters, setActiveFilters] = useState([])
  const [direction, setDirection] = useState("DESC")
  const [sortName, setSortName] = useState("id")

  // Action Upon Submit
  const [actionSelect, setActionSelect] = useState(null)
  const debouncedLocalSlider = useDebounce(sliderValue, 300)
  const debouncedSearch = useDebounce(searchComponentValue, 500)
  //change the dropdown value
  const handleChangeActive = (event) => {
    setActiveValue(event.target.value)
    setCurrentPage(0)
  }
  const handleActionSelect = (event) => {
    setActionSelect(event.target.value)
    setCurrentPage(0)
  }
  const handleChangeClient = (event) => {
    setClientValue(event.target.value)
    setCurrentPage(0)
  }

  const handleChangeAccountCreation = (event) => {
    setAccountCreationValue(event.target.value)
    setCurrentPage(0)
  }

  const handleChangeUserType = (event) => {
    setUserTypeValue(event.target.value)
    setCurrentPage(0)
  }

  //useEffect to add active filters if search is used
  useEffect(() => {
    if (searchValue !== "" && !activeFilters.includes("search")) {
      setActiveFilters([...activeFilters, "search"])
    }
    if (searchValue === "" && activeFilters.includes("search")) {
      setActiveFilters(activeFilters.filter((x) => x !== "search"))
    }
  }, [searchValue])

  //useEffect to add active filters if active dropdown is used
  useEffect(() => {
    if (activeValue !== "all" && !activeFilters.includes("active")) {
      setActiveFilters([...activeFilters, "active"])
    }
    if (activeValue === "all" && activeFilters.includes("active")) {
      setActiveFilters(activeFilters.filter((x) => x !== "active"))
    }
  }, [activeValue])

  useEffect(() => {
    if (clientValue !== "all" && !activeFilters.includes("clientId")) {
      setActiveFilters([...activeFilters, "clientId"])
    }
    if (clientValue === "all" && activeFilters.includes("clientId")) {
      setActiveFilters(activeFilters.filter((x) => x !== "clientId"))
    }
  }, [clientValue])

  useEffect(() => {
    if (
      accountCreationValue !== "all" &&
      !activeFilters.includes("accountCreation")
    ) {
      setActiveFilters([...activeFilters, "accountCreation"])
    }
    if (
      accountCreationValue === "all" &&
      activeFilters.includes("accountCreation")
    ) {
      setActiveFilters(activeFilters.filter((x) => x !== "accountCreation"))
    }
  }, [accountCreationValue])

  useEffect(() => {
    if (userTypeValue !== "user" && !activeFilters.includes("user_type")) {
      setActiveFilters([...activeFilters, "user_type"])
    }
    if (userTypeValue === "user" && activeFilters.includes("user_type")) {
      setActiveFilters(activeFilters.filter((x) => x !== "user_type"))
    }
  }, [userTypeValue])

  //useEffect to add courseCount if slider is used
  useEffect(() => {
    if (
      coursesCount !== null &&
      (debouncedSliderValue[0] !== 0 ||
        debouncedSliderValue[1] !== coursesCount) &&
      !activeFilters.includes("courseCount")
    ) {
      setActiveFilters([...activeFilters, "courseCount"])
    }

    if (
      coursesCount !== null &&
      debouncedSliderValue[0] === 0 &&
      debouncedSliderValue[1] === coursesCount &&
      activeFilters.includes("courseCount")
    ) {
      setActiveFilters(activeFilters.filter((x) => x !== "courseCount"))
    }
  }, [debouncedSliderValue])

  //handler change for slider
  const handleChangeSlider = (event, newValue) => {
    setSliderValue(newValue)
    setCurrentPage(0)
  }
  //value for the slider max and min
  function valueText(value) {
    return value
  }

  //useEffect to change the badge number if a filter or search is used
  useEffect(() => {
    setBadgeState(Number(activeFilters.length))
  }, [activeFilters])

  //reset filters and searching values
  const clearFilters = () => {
    setSearchComponentValue("")
    setActiveValue("all")
    setClientValue("all")
    setAccountCreationValue("all")
    setSearchValue("")
    setActiveFilters([])
    setSliderValue([0, coursesCount])
    setDebouncedSliderValue([0, coursesCount])
    setUserTypeValue("user")
    setCurrentPage(0)
  }
  //clearFilters when we arrive to prevent errors when we change to admin tab
  useEffect(() => {
    clearFilters()
  }, [])

  //set slider using debounce
  useEffect(() => {
    setDebouncedSliderValue(sliderValue)
  }, [debouncedLocalSlider])

  useEffect(() => {
    setSearchValue(searchComponentValue)
    setCurrentPage(0)
  }, [debouncedSearch])

  let value = Object.keys(SORT_OPTIONS).map((opt) => {
    if (SORT_OPTIONS[opt] === sort.name) {
      return SORT_OPTIONS[opt]
    }
  })
  const flatValue = value.filter(function (element) {
    return element !== undefined
  })

  const filtersContent = () => {
    return (
      // <Box className={classes.filtersContainer}>
      <div className="w-full relative md:overflow-auto">
        <div className="grid grid-cols-2 gap-4 text-white sm:p-4">
          {!smDown && (
            <>
              <div className="flex justify-between col-span-2 mb-4">
                <div className="flex items-center space-x-4 mb-2">
                  <Typography variant="h3" className={classes.title}>
                    Filters
                  </Typography>
                  {badgeState > 0 && (
                    <Badge badgeContent={badgeState} color="primary" />
                  )}
                </div>
                <PermissionsWrapper permissionGroup="admin">
                  <Button
                    className="border border-[red]"
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={() => {
                      openDialog("createAppUserDialog")
                    }}
                  >
                    Add User
                  </Button>
                </PermissionsWrapper>
              </div>
              {/* <div className="col-span-2">
                <Typography>
                  Showing {totalShowing} of {totalNumber}
                </Typography>
              </div> */}
            </>
          )}

          {smDown && (
            <PermissionsWrapper permissionGroup="admin">
              <div className="col-span-2 flex justify-end -mt-4 -mb-2">
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={() => {
                    openDialog("createAppUserDialog")
                  }}
                >
                  Add User
                </Button>
              </div>
            </PermissionsWrapper>
          )}

          <div className="xl:col-span-2">
            <Typography variant="h4" className={classes.subtitle}>
              Search
            </Typography>
            <TextField
              value={searchComponentValue}
              placeholder="Search by name, email, or user ID"
              variant="filled"
              size="small"
              fullWidth
              // className={classes.formControl}
              InputProps={{
                classes: {
                  root: classes.inputBackgroundColor,
                },
                startAdornment: (
                  <InputAdornment
                    position="start"
                    style={{ marginLeft: "2px", opacity: "0.3", marginTop: 0 }}
                  >
                    <Search />
                  </InputAdornment>
                ),
              }}
              onChange={(e) => {
                setSearchComponentValue(e.target.value)
                if (e.target.value === "") {
                  setSearchValue("")
                }
              }}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  setSearchValue(e.target.value)
                }
              }}
            />
          </div>

          <div className="xl:col-span-2">
            <Typography variant="h4" className={classes.subtitle}>
              Active
            </Typography>
            <FormControl variant="filled" className={classes.formControl}>
              <Select
                style={{ backgroundColor: "#0929582B", color: "#fff" }}
                className={classes.select}
                classes={{ select: classes.selectRoot }}
                labelId="demo-simple-select-filled-label"
                id="demo-simple-select-filled"
                value={activeValue}
                onChange={handleChangeActive}
                inputProps={{
                  classes: {
                    icon: classes.icon,
                  },
                }}
              >
                <MenuItem value={"all"}>
                  <Typography
                    variant="subtitle1"
                    style={{ fontWeight: "bold" }}
                  >
                    All
                  </Typography>
                </MenuItem>
                <MenuItem value={"30 days"}>
                  <Typography
                    variant="subtitle1"
                    style={{ fontWeight: "bold" }}
                  >
                    Last 30 days
                  </Typography>
                </MenuItem>
                <MenuItem value={"7 days"}>
                  <Typography
                    variant="subtitle1"
                    style={{ fontWeight: "bold" }}
                  >
                    Last 7 days
                  </Typography>
                </MenuItem>
                <MenuItem value={"3 days"}>
                  <Typography
                    variant="subtitle1"
                    style={{ fontWeight: "bold" }}
                  >
                    Last 3 days
                  </Typography>
                </MenuItem>
                <MenuItem value={"1 day"}>
                  <Typography
                    variant="subtitle1"
                    style={{ fontWeight: "bold" }}
                  >
                    Last 1 day
                  </Typography>
                </MenuItem>
                <MenuItem value={"1 hour"}>
                  <Typography
                    variant="subtitle1"
                    style={{ fontWeight: "bold" }}
                  >
                    Last 1 hour
                  </Typography>
                </MenuItem>
              </Select>
            </FormControl>
          </div>

          <div className="xl:col-span-2">
            <Typography variant="h4" className={classes.subtitle}>
              Filter by Client
            </Typography>
            <FormControl variant="filled" className={classes.formControl}>
              <Select
                style={{ backgroundColor: "#0929582B", color: "#fff" }}
                className={classes.select}
                classes={{ select: classes.selectRoot }}
                labelId="demo-simple-select-filled-label"
                id="demo-simple-select-filled"
                value={clientValue}
                onChange={handleChangeClient}
                inputProps={{
                  classes: {
                    icon: classes.icon,
                  },
                }}
              >
                <MenuItem value={"all"}>
                  <Typography
                    variant="subtitle1"
                    style={{ fontWeight: "bold" }}
                  >
                    All
                  </Typography>
                </MenuItem>
                {clients.map((client) => {
                  return (
                    <MenuItem key={client.id} value={client.id}>
                      <Typography
                        variant="subtitle1"
                        style={{ fontWeight: "bold" }}
                      >
                        {client.name}
                      </Typography>
                    </MenuItem>
                  )
                })}
              </Select>
            </FormControl>
          </div>

          <div className="xl:col-span-2">
            <Typography variant="h4" className={classes.subtitle}>
              Filter by User Type
            </Typography>
            <FormControl variant="filled" className={classes.formControl}>
              <Select
                style={{ backgroundColor: "#0929582B", color: "#fff" }}
                className={classes.select}
                classes={{ select: classes.selectRoot }}
                value={userTypeValue}
                onChange={handleChangeUserType}
                inputProps={{
                  classes: {
                    icon: classes.icon,
                  },
                }}
              >
                <MenuItem value="all">
                  <Typography
                    variant="subtitle1"
                    style={{ fontWeight: "bold" }}
                  >
                    All
                  </Typography>
                </MenuItem>
                <MenuItem value="test">
                  <Typography
                    variant="subtitle1"
                    style={{ fontWeight: "bold" }}
                  >
                    Test
                  </Typography>
                </MenuItem>
                <MenuItem value="user">
                  <Typography
                    variant="subtitle1"
                    style={{ fontWeight: "bold" }}
                  >
                    User
                  </Typography>
                </MenuItem>
              </Select>
            </FormControl>
          </div>

          <div className="xl:col-span-2">
            <Typography
              variant="h4"
              className="md:mb-2 font-bold flex flex-col sm:flex-row items-start sm:justify-between"
            >
              Filter by Account Creation
            </Typography>

            <FormControl variant="filled" className={classes.formControl}>
              <Select
                style={{ backgroundColor: "#0929582B", color: "#fff" }}
                className={classes.select}
                classes={{ select: classes.selectRoot }}
                value={accountCreationValue}
                onChange={handleChangeAccountCreation}
                inputProps={{
                  classes: {
                    icon: classes.icon,
                  },
                }}
              >
                {Object.keys(ACCOUNT_CREATION_OPTIONS).map((key, i) => {
                  return (
                    <MenuItem value={ACCOUNT_CREATION_OPTIONS[key]} key={i}>
                      <Typography
                        variant="subtitle1"
                        style={{ fontWeight: "bold" }}
                      >
                        {key}
                      </Typography>
                    </MenuItem>
                  )
                })}
              </Select>
            </FormControl>
          </div>

          <div className="xl:col-span-2">
            <Typography
              variant="h4"
              className="md:mb-2 font-bold flex flex-col sm:flex-row items-start sm:justify-between"
            >
              Sort Users By:
              <button
                className="top-0 left-0 right-0 sm:right-4 text-base hover:underline"
                onClick={() => {
                  setSort({
                    name: sortName,
                    direction: direction === "ASC" ? "DESC" : "ASC",
                  })
                  setDirection(direction === "ASC" ? "DESC" : "ASC")
                  setCurrentPage(0)
                }}
              >
                {direction === "ASC" ? (
                  <span>
                    Ascending
                    <KeyboardArrowUpIcon />
                  </span>
                ) : (
                  <span>
                    Descending
                    <KeyboardArrowDownIcon />
                  </span>
                )}
              </button>
            </Typography>
            <FormControl variant="filled" className={classes.formControl}>
              <Select
                style={{ backgroundColor: "#0929582B", color: "#fff" }}
                className={classes.select}
                classes={{ select: classes.selectRoot }}
                value={flatValue[0]}
                onChange={(e) => {
                  setSort({ name: e.target.value, direction: direction })
                  setSortName(e.target.value)
                  setCurrentPage(0)
                }}
                inputProps={{
                  classes: {
                    icon: classes.icon,
                  },
                }}
              >
                {Object.keys(SORT_OPTIONS).map((key, i) => {
                  return (
                    <MenuItem value={SORT_OPTIONS[key]} key={i}>
                      <Typography
                        variant="subtitle1"
                        style={{ fontWeight: "bold" }}
                      >
                        {key}
                      </Typography>
                    </MenuItem>
                  )
                })}
              </Select>
            </FormControl>
          </div>

          <div className="xl:col-span-2">
            <Typography variant="h4" className={classes.subtitle}>
              Courses Completed
            </Typography>
            <Slider
              value={sliderValue}
              onChange={handleChangeSlider}
              defaultValue={0}
              step={1}
              min={0}
              max={coursesCount}
              valueLabelDisplay="on"
              aria-labelledby="range-slider"
              getAriaValueText={valueText}
              classes={{
                root: classes.sliderColor,
              }}
            />
          </div>
        </div>

        <Box className={clsx(classes.clearButton, "col-span-2")}>
          <Button
            classes={{ text: classes.buttonColor }}
            onClick={clearFilters}
          >
            Clear
          </Button>
        </Box>
      </div>
    )
  }

  if (smDown) {
    return (
      <Accordion>
        <AccordionSummary
          style={{ backgroundColor: "#17A5B1" }}
          expandIcon={<ExpandMore style={{ color: "white" }} />}
          onClick={() => setAccordionExpanded(!accordionExpanded)}
        >
          <Box display="flex" width="100%">
            <Typography
              variant="h3"
              style={{ color: "#fff" }}
              className={classes.title}
            >
              Filters
            </Typography>
            {badgeState > 0 && (
              <Badge
                className="mt-3 ml-4"
                badgeContent={badgeState}
                color="primary"
              />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails style={{ backgroundColor: "#17A5B1" }}>
          {filtersContent()}
        </AccordionDetails>
      </Accordion>
    )
  } else {
    return filtersContent()
  }
}

UsersFilter.propTypes = {
  totalNumber: PropTypes.number,
  totalShowing: PropTypes.number,
  setAccordionExpanded: PropTypes.func,
  accordionExpanded: PropTypes.bool,
  currentPage: PropTypes.number,
  setCurrentPage: PropTypes.func,
}

export default UsersFilter
